import { useRef, useState, type RefObject } from "react";
import {
  createCsvBlob,
  createExcelBlob,
  createPngBlob,
  createSvgBlob,
  downloadBlob,
  resultFileName,
  type ExportColumn,
  type ExportRow
} from "./result-export.js";
import { api } from "./api.js";

interface Props {
  title: string;
  columns: ExportColumn[];
  rows: ExportRow[];
  chartRef?: RefObject<SVGSVGElement | null>;
  actionId: string;
  objectApiName: string;
  resultKind: "detail" | "grouped" | "chart";
  allowedFormats?: Array<"excel" | "csv" | "png" | "svg">;
}

export function ResultDownloadMenu({ title, columns, rows, chartRef, actionId, objectApiName, resultKind, allowedFormats }: Props) {
  const [status, setStatus] = useState("");
  const menuRef = useRef<HTMLDetailsElement>(null);
  const runDownload = async (format: "excel" | "csv" | "png" | "svg") => {
    try {
      await api.authorizeResultExport({ actionId, objectApiName, resultKind, format, recordCount: rows.length });
      if (format === "excel") downloadBlob(createExcelBlob(title, columns, rows), resultFileName(title, "xls"));
      if (format === "csv") downloadBlob(createCsvBlob(columns, rows), resultFileName(title, "csv"));
      if (format === "svg" && chartRef?.current) downloadBlob(createSvgBlob(chartRef.current), resultFileName(title, "svg"));
      if (format === "png" && chartRef?.current) downloadBlob(await createPngBlob(chartRef.current), resultFileName(title, "png"));
      setStatus(`${format === "excel" ? "Excel" : format.toUpperCase()} 文件已生成`);
      if (menuRef.current) menuRef.current.open = false;
    } catch (error) {
      setStatus(error instanceof Error ? error.message : "文件生成失败");
    }
  };

  return <details ref={menuRef} className="result-download-menu">
    <summary aria-label="下载当前结果">
      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3v11m0 0 4-4m-4 4-4-4M5 17v3h14v-3" /></svg>
      <span>下载</span>
    </summary>
    <div className="result-download-menu__panel" role="menu">
      <strong>下载本次结果</strong>
      <small>仅包含当前用户权限下返回的 {rows.length} 条数据</small>
      {chartRef && allowedFormats?.includes("png") !== false ? <button type="button" role="menuitem" onClick={() => void runDownload("png")}>PNG 图片<span>高清图表</span></button> : null}
      {chartRef && allowedFormats?.includes("svg") !== false ? <button type="button" role="menuitem" onClick={() => void runDownload("svg")}>SVG 图片<span>可缩放图表</span></button> : null}
      {allowedFormats?.includes("excel") !== false ? <button type="button" role="menuitem" onClick={() => void runDownload("excel")}>Excel<span>业务查看与筛选</span></button> : null}
      {allowedFormats?.includes("csv") !== false ? <button type="button" role="menuitem" onClick={() => void runDownload("csv")}>CSV<span>系统交换格式</span></button> : null}
      <span className={status ? "result-download-menu__status" : "visually-hidden"} aria-live="polite">{status}</span>
    </div>
  </details>;
}
